import React, { useEffect, useRef, useState } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { isArray } from 'lodash';
import { HomeFormFlowStyles, HomeFormStyles } from './styles/HomeFormFlow.styles';
// import { CenteredDivTitle } from '../../common/components/CenteredPageTitle.styles';
import InputPrivacy from '../../common/componentsV2/inputs/InputPrivacy';
import {
  getFastQuoteData,
  houseGetCustomerData, houseGetInitialData, houseSubmitAnswers, resetHouseForm,
} from '../../features/forms/formsActions';
import { greeniaPrivacyPolicy, modifyRequestData } from '../../services/axios-client/axeCommons';
import { answersHomeFieldsBySteps, homeSchema } from './HomeFormFlow.validations';
import {
  NON_TOUCHABLE_FIELDS,
  isHomeFieldValid,
  getBlockingFields,
  buildMissingFieldsMessage,
} from './validations/homeStepFields';
import generateFiscalCode from '../../common/helpers/generateFiscalCode';
import { generalAlertError } from '../../common/utils/alerts';
import QuoteGenerateLoading from '../../common/componentsV2/elements/QuoteGenerateLoading';
import ProgressBar from '../../common/componentsV2/ProgressBar';
import DatiContrattualiStep from './components/stepForms/DatiContrattualiStep';
import DatiAssicuratoStep from './components/stepForms/DatiAssicuratoStep';
import DatiUbicazioneStep from './components/stepForms/DatiUbicazioneStep';
import GaranzieStep from './components/stepForms/GaranzieStep';
import { garanzie_assicurato, garanzie_ubicazione } from '../../utils/globalCasaConstants';
import { V2FontImport } from '../../common/formInputsV2/V2Fonts';
import { FormLayout } from '../../common/formInputsV2/FormLayout.styles';
import { SurfaceCardHeading } from '../../common/formInputsV2/Card.styles';
import { FooterNav } from '../../common/formInputsV2/FooterNav.styles';
import { PrimaryButton, SecondaryButton } from '../../common/formInputsV2/Button.styles';
import { sonnerErrorAlert } from '../../common/utils/sonnerAlerts';
import PageLoading from '../../common/componentsV2/elements/PageLoading';
// import moment from 'moment';

const titles = {
  1: 'Dati Contrattuali',
  2: 'Dati Assicurato',
  3: 'Dati Ubicazione',
  4: 'Garanzie',
};

function HomeFormFlow() {
  const navigate = useNavigate();
  const location = useLocation();
  const { fastQuoteId } = useParams();
  const [fastQuoteData, setFastQuoteData] = useState(null);
  const [index, setIndex] = useState(1);
  const skipCheckGenderRef = useRef(null);

  const state = useSelector((store) => store.forms.house);
  const {
    loading,
    states,
    municipalities,
    formData,
    dataPrivacy,
    loadingQuotes,
  } = state;
  const {
    register,
    formState: { errors, touchedFields },
    setValue,
    trigger,
    // getValues,
    watch,
    control,
  } = useForm({
    mode: 'onTouched',
    resolver: yupResolver(homeSchema),
    shouldFocusError: false,
    defaultValues: {
      product: 'casa',
      source: 'greenia.it',
      policy_effective_date: '',
      privacies: [],
      name: '',
      surname: '',
      email: '',
      fiscal_code: '',
      residence_province_code: '',
      address: '',
      house_number: '',
      postal_code: '',
      phone: '',
      gender: '',
      date_of_birth: '',
      country_of_birth_code: 'Z000',
      province_of_birth_code: null,
      commune_of_birth_code: null,
      born_abroad: false,
      residence_commune_code: '',
      active_customer_id: null,
      existing_customer: false,
      owner_typology: '',
      square_meters: '',
      house_type: '',
      business_name: '',
      vat_number: '',
      company_type: null,
      housing_type: '',
      presence_of_condominium_policy: '',
      interested_in_building_fire_coverage: '',
      interested_in_contents_fire_coverage: '',
      building_value: '',
      insured_amount_contents_fire: '',
      residential_use_types: '',
      floor_level: '',
      construction_feature_types: '',
      construction_year: '',
      house_commune_code: '',
      house_province_code: '',
      current_postal_code: '',
      current_address: '',
      current_house_number: '',

      insurance_formula: '',
      insured_guarantees: [],
      location_guarantees: [],
    },
  });

  const {
    fields, replace, update,
  } = useFieldArray({
    control,
    name: 'insured_guarantees',
  });

  const { fields: fieldsLocation, replace: replaceLocation, update: updateLocation } = useFieldArray({
    control,
    name: 'location_guarantees',
  });

  const watchedGuarantees = useWatch({
    control,
    name: 'location_guarantees',
  });

  const formDataFlow = watch();

  const updateFormData = (data) => {
    const keys = Object.keys(data);
    keys.forEach((key) => {
      setValue(key, data[key], {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    });
    trigger(keys);
  };

  const isValid = (name) => {
    if (!isHomeFieldValid(errors, touchedFields, name)) {
      return false;
    }
    // "Valid" (green) means confirmed-good, not merely "touched and not yet
    // errored" — onTouched marks a field touched on its first blur before
    // validation runs, which would otherwise flash an empty required <select>
    // green just for being opened and closed.
    const value = formDataFlow[name];
    return value !== undefined && value !== null && value !== '';
  };

  const scrollToFirstError = () => {
    setTimeout(() => {
      const el = document.querySelector(
        '.is-invalid, [aria-invalid="true"], .general-input-error, .invalid-feedback.d-block',
      );
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 300);
  };

  // Pre-filled fields (VAT/fast-quote/"Modifica" reload) don't get marked
  // touched by react-hook-form's own setValue/trigger, so they'd otherwise
  // stay neutral (no green) until manually interacted with — see the same
  // fix already shipped for the Commercial wizard.
  const autoValidatePrefilledFields = async (fieldsToCheck) => {
    const filled = (fieldsToCheck || []).filter((name) => {
      const value = watch(name);
      return value !== undefined && value !== null && value !== '';
    });
    if (filled.length === 0) return;

    filled
      .filter((name) => !NON_TOUCHABLE_FIELDS.includes(name))
      .forEach((name) => {
        setValue(name, watch(name), { shouldTouch: true, shouldValidate: false });
      });

    await trigger(filled);
  };

  useEffect(() => {
    autoValidatePrefilledFields(answersHomeFieldsBySteps[index - 1] || []);
  }, [index]);

  const privacyItemChangeHandler = (value, item) => {
    if (!value.target.checked) {
      const newArray = [...formDataFlow.privacies];
      const index = newArray.indexOf(item.id);
      newArray.splice(index, 1);
      updateFormData({
        privacies: newArray,
      });
      return;
    }
    const answersNewArray = [...formDataFlow.privacies];
    answersNewArray.push(item.id);
    updateFormData({
      privacies: answersNewArray,
    });
  };

  const getCustomerData = async (customerId) => {
    houseGetCustomerData(customerId);
  };

  useEffect(() => {
    houseGetInitialData();

    return () => {
      resetHouseForm();
    };
  }, []);

  useEffect(() => {
    if (location.state !== null && location.state.activeCustomerId !== undefined) {
      getCustomerData(location.state.activeCustomerId);
    }
  }, []);

  useEffect(() => {
    // if (formDataFlow?.residential_use_types === '144') {
    //   // Reset building coverage only
    //   updateFormData({
    //     interested_in_contents_fire_coverage: '0',
    //     interested_in_building_fire_coverage: '1',

    //   });
    // }

    // if (['141', '142', '143'].includes(formDataFlow.residential_use_types)) {
    //   // Always set contents fire coverage = Sì
    //   updateFormData({
    //     interested_in_contents_fire_coverage: '',
    //     interested_in_building_fire_coverage: '',
    //   });
    // }

    if (['145', '146'].includes(formDataFlow.residential_use_types)) {
      // Always set contents fire coverage = Sì
      updateFormData({
        interested_in_contents_fire_coverage: '1',
        interested_in_building_fire_coverage: '0',
      });
    }

    if (['117', '118'].includes(formDataFlow.housing_type)) {
      updateFormData({
        floor_level: '',
        presence_of_condominium_policy: '',
      });
    }
  }, [formDataFlow.residential_use_types, formDataFlow.housing_type]);

  const getFiscalCode = (birthDate) => {
    const d = birthDate.split('-');
    const fData = {
      name: formDataFlow.name,
      surname: formDataFlow.surname,
      gender: formDataFlow.gender,
      day: d[2],
      month: d[1],
      year: d[0],
    };
    if (formDataFlow.born_abroad) {
      fData.birthplace = states.filter(
        (s) => s.state_code === formDataFlow.country_of_birth_code,
      )[0].name;
      fData.birthplaceProvincia = 'EE';
    } else {
      fData.birthplace = municipalities.filter(
        (municipality) => municipality.cadastral_code === formDataFlow.commune_of_birth_code,
      )[0].name;
      fData.birthplaceProvincia = formDataFlow.province_of_birth_code;
    }
    return generateFiscalCode(fData);
  };

  const [showPrivacyErrors, setShowPrivacyErrors] = useState(false);

  const validateDataPrivacy = () => {
    let { error, message } = '';

    dataPrivacy.forEach((privacy) => {
      if (privacy.required === 1) {
        const userDataArray = { ...formDataFlow }; // make a separate copy of the object
        const index = userDataArray.privacies.indexOf(privacy.id);

        if (index < 0) {
          error = true;
          message = 'Tutti i campi sono obbligatori! Tutta la privacy con la stella deve essere controllata';
        }
      }
    });
    return { error, message };
  };

  const submitForm = (formDataFlow) => {
    const { error, message } = validateDataPrivacy();

    if (error) {
      setShowPrivacyErrors(true);
      sonnerErrorAlert(message);
      scrollToFirstError();
      return;
    }

    const _userData = { ...formDataFlow };

    if (_userData.born_abroad && _userData.country_of_birth_code === 'Z000') {
      window.scrollTo(0, 0);
      sonnerErrorAlert('Non puoi selezionare Italia come stato estero!');
      return;
    }

    if (_userData.born_abroad && (_userData.country_of_birth_code === null || _userData.country_of_birth_code === '' || _userData.country_of_birth_code == undefined)) {
      window.scrollTo(0, 0);
      sonnerErrorAlert('Devi selezionare uno stato estero!');
      return;
    }

    if (_userData.date_of_birth !== '' && _userData.date_of_birth !== null) {
      _userData.date_of_birth = moment(_userData.date_of_birth).format('YYYY-MM-DD');
    } else {
      _userData.date_of_birth = null;
    }
    _userData.policy_effective_date = moment(_userData.policy_effective_date).format('YYYY-MM-DD');
    _userData.fiscal_code = _userData.gender !== 'G' ? getFiscalCode(_userData.date_of_birth) : null;
    delete _userData.existing_customer;
    houseSubmitAnswers(_userData, navigate);
  };

  const prevButton = () => {
    if (index > 1) {
      setIndex(index - 1);
    }
  };

  const nextButton = async () => {
    if (index !== 4) {
      const stepFields = answersHomeFieldsBySteps[index - 1];
      const res = await trigger(stepFields, {
        shouldTouch: false,
        shouldDirty: false,
        shouldValidate: true,
      });

      if (res !== true) {
        const blocking = getBlockingFields(homeSchema, formDataFlow, stepFields);
        sonnerErrorAlert(buildMissingFieldsMessage(blocking));
        scrollToFirstError();
        return;
      }
    }

    if (index === 1) {
      if (formDataFlow.born_abroad && formDataFlow.country_of_birth_code === 'Z000') {
        window.scrollTo(0, 0);
        sonnerErrorAlert('Non puoi selezionare Italia come stato estero!');
        return;
      }

      if (
        formDataFlow.born_abroad
        && (formDataFlow.country_of_birth_code === null
          || formDataFlow.country_of_birth_code === ''
          || formDataFlow.country_of_birth_code == undefined)
      ) {
        window.scrollTo(0, 0);
        sonnerErrorAlert('Devi selezionare uno stato estero!');
        return;
      }

      const validation = validateDataPrivacy();
      if (validation.error) {
        setShowPrivacyErrors(true);
        sonnerErrorAlert(validation.message);
        scrollToFirstError();
        return;
      }
    }

    if (index === 2) {
      const firstGuarantee = formDataFlow.insured_guarantees && formDataFlow.insured_guarantees[0];
      if (firstGuarantee) {
        if (firstGuarantee.disabled && !firstGuarantee.selected) {
          sonnerErrorAlert('La prima garanzia obbligatoria deve essere selezionata.');
          return;
        }
        // Check if all required parameters are filled if selected
        if (firstGuarantee.selected && Array.isArray(firstGuarantee.parametersConfig) && firstGuarantee.parametersConfig.length > 0) {
          const missingParam = firstGuarantee.parametersConfig.some(
            (param) => !firstGuarantee.parameters || !firstGuarantee.parameters[param.cod_param],
          );
          if (missingParam) {
            generalAlertError('Tutti i parametri obbligatori della garanzia devono essere compilati.');
            return;
          }
        }
      }
    }

    if (index === 4) {
      // Final submit validates every step so the broker is sent straight to the
      // wrong field (with a clear message) instead of hitting a cryptic backend
      // error — the jump-to-Garanzie entry point skips steps 1-3 entirely.
      for (let step = 0; step < answersHomeFieldsBySteps.length; step++) {
        // eslint-disable-next-line no-await-in-loop
        const stepValid = await trigger(answersHomeFieldsBySteps[step]);
        if (!stepValid) {
          setIndex(step + 1);
          window.scrollTo(0, 0);
          const blocking = getBlockingFields(homeSchema, formDataFlow, answersHomeFieldsBySteps[step]);
          generalAlertError(
            `Controlla il passaggio "${titles[step + 1]}": ${buildMissingFieldsMessage(blocking)}`,
          );
          scrollToFirstError();
          return;
        }
      }

      const _userData = { ...formDataFlow };

      for (let x = 0; x < _userData.insured_guarantees.length; x++) {
        // Guarantees without parameters persist as [] (PHP empty array); the
        // backend expects an object, so send {} exactly like the create flow.
        if (Array.isArray(_userData.insured_guarantees[x].parameters)) {
          _userData.insured_guarantees[x].parameters = {};
        }
        delete _userData.insured_guarantees[x].parametersConfig;
        delete _userData.insured_guarantees[x].desc_garanzia;
        delete _userData.insured_guarantees[x].disabled;
        delete _userData.insured_guarantees[x].id;
      }

      for (let y = 0; y < _userData.location_guarantees.length; y++) {
        if (Array.isArray(_userData.location_guarantees[y].parameters)) {
          _userData.location_guarantees[y].parameters = {};
        }
        delete _userData.location_guarantees[y].parametersConfig;
        delete _userData.location_guarantees[y].desc_garanzia;
        delete _userData.location_guarantees[y].disabled;
        delete _userData.location_guarantees[y].id;
        delete _userData.location_guarantees[y].group_garanzia;
      }
      submitForm(_userData);

      return;
    }

    setIndex(index + 1);
    setTimeout(() => window.scrollTo(0, 0), 500);
  };

  useEffect(() => {
    if (skipCheckGenderRef.current === true) {
      skipCheckGenderRef.current = false;
      return;
    }

    const triggerValidation = {
      shouldTouch: true,
      shouldDirty: true,
      shouldValidate: true,
    };

    if (formDataFlow.gender === 'G') {
      setValue('date_of_birth', null);
      setValue('country_of_birth_code', null);
      setValue('province_of_birth_code', null);
      setValue('commune_of_birth_code', null);
      setValue('born_abroad', false);
      setValue('name', null);
      setValue('surname', null);
    } else if (['M', 'F'].includes(formDataFlow.gender) && formDataFlow.date_of_birth === null) {
      setValue('date_of_birth', '', triggerValidation);
      setValue('country_of_birth_code', 'Z000', triggerValidation);
      setValue('province_of_birth_code', null, triggerValidation);
      setValue('commune_of_birth_code', null, triggerValidation);
      setValue('born_abroad', false, triggerValidation);
      setValue('name', '', triggerValidation);
      setValue('surname', '', triggerValidation);
    }
  }, [formDataFlow.gender, formDataFlow.date_of_birth]);

  // useEffect(() => {
  //   const values = getValues();

  //   if (values !== undefined) {
  //     const objectKeys = Object.keys(values);

  //     objectKeys.forEach((key) => {
  //       if (values[key] !== '' && values[key] !== null) {
  //         setValue(key, values[key], {
  //           shouldTouch: true,
  //           shouldDirty: true,
  //           shouldValidate: true,
  //         });
  //       }
  //     });
  //   }
  // }, []);

  useEffect(() => {
    const callAsync = async () => {
      if (
        location.state !== null
        && location.state.requestToken !== undefined
      ) {
        skipCheckGenderRef.current = true;
        const {
          data: { data: { userData } },
        } = await modifyRequestData(location.state.requestToken);
        let oldGaranzie = [];
        if (userData.insurance_formula && userData.insured_guarantees && userData.insured_guarantees.length > 0) {
          oldGaranzie = userData.insured_guarantees.map((g) => {
            const found = garanzie_assicurato.find((garanzia) => garanzia.cod_garanzia === g.cod_garanzia);

            let params = {};

            if (!isArray(g.parameters)) {
              params = g.parameters || {};
            }

            return {
              cod_garanzia: g.cod_garanzia,
              desc_garanzia: found ? found.desc_garanzia : '',
              selected: g.selected,
              parameters: params,
              parametersConfig: found ? found.parameters || [] : [],
              disabled: found && ((userData.insurance_formula === '111' && g.cod_garanzia === 'RCVP') || (userData.insurance_formula === '112' && g.cod_garanzia === 'RCVC')),
            };
          });
        }
        let oldUbicazioneGaranzie = [];
        if (userData.location_guarantees && userData.location_guarantees.length > 0) {
          oldUbicazioneGaranzie = userData.location_guarantees.filter((g) => garanzie_ubicazione.some((garanzia) => garanzia.cod_pacchetto === g.cod_pacchetto)).map((g) => {
            const found = garanzie_ubicazione.find((garanzia) => garanzia.cod_pacchetto === g.cod_pacchetto);
            const isRequiredIncendioFabbricato = userData.interested_in_building_fire_coverage === '1' && g.cod_pacchetto === 'INCF';
            const isRequiredIncendioContenuto = userData.interested_in_contents_fire_coverage === '1' && g.cod_pacchetto === 'INCC';
            const isRequired = isRequiredIncendioFabbricato || isRequiredIncendioContenuto;
            let params = {};

            if (!isArray(g.parameters)) {
              params = g.parameters || {};
            }

            if (found && found.parameters && found.parameters.length > 0) {
              const validKeys = found.parameters.map((p) => p.cod_param);
              params = Object.fromEntries(
                Object.entries(params).filter(([key]) => validKeys.includes(key)),
              );
            }

            if (isRequiredIncendioFabbricato) {
              return {
                cod_garanzie: g.cod_garanzie,
                cod_pacchetto: g.cod_pacchetto === 'null' ? null : g.cod_pacchetto,
                desc_garanzia: found ? found.desc_garanzia : '',
                group_garanzia: found ? found.group_garanzia : '',
                selected: g.selected,
                disabled: isRequiredIncendioFabbricato,
                parameters: params,
                parametersConfig: found ? found.parameters || [] : [],
                building_value: userData.building_value || '',
              };
            }

            if (isRequiredIncendioContenuto) {
              return {
                cod_garanzie: g.cod_garanzie,
                cod_pacchetto: g.cod_pacchetto === 'null' ? null : g.cod_pacchetto,
                desc_garanzia: found ? found.desc_garanzia : '',
                group_garanzia: found ? found.group_garanzia : '',
                selected: g.selected,
                disabled: isRequiredIncendioFabbricato,
                parameters: params,
                parametersConfig: found ? found.parameters || [] : [],
                building_value: userData.insured_amount_contents_fire || '',
              };
            }

            return {
              cod_garanzie: g.cod_garanzie,
              cod_pacchetto: g.cod_pacchetto === 'null' ? null : g.cod_pacchetto,
              desc_garanzia: found ? found.desc_garanzia : '',
              group_garanzia: found ? found.group_garanzia : '',
              selected: g.selected,
              disabled: isRequired,
              parameters: params,
              parametersConfig: found ? found.parameters || [] : [],
              building_value: g.building_value || '',
            };
          });
        }

        updateFormData(userData);

        replace(oldGaranzie);
        replaceLocation(oldUbicazioneGaranzie);

        // updateFormData only touches top-level keys — mark every known
        // field across all steps touched + validated now, regardless of
        // which step is currently showing, same fix as Commercial's
        // modify-flow.
        await autoValidatePrefilledFields(answersHomeFieldsBySteps.flat());

        if (location.state.goToGuarantees) {
          setIndex(4);
        }
      }
    };
    callAsync();
  }, [location]);

  useEffect(() => {
    if (fastQuoteId && fastQuoteData === null) {
      getFastQuoteData(fastQuoteId).then((fast_quote_response) => {
        setFastQuoteData(fast_quote_response.data);
      }).catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error fetching fast quote data:', error);
      });
    }

    if (fastQuoteData) {
      setValue('building_value', fastQuoteData.reference_response.building_value?.toString());
      autoValidatePrefilledFields(['building_value']);
    }
  }, [fastQuoteData]);

  useEffect(() => {
    window.scrollTo(0, 0, { behavior: 'smooth' });
  }, [index]);

  useEffect(() => {
    if (formDataFlow.insurance_formula === '113') {
      updateFormData({
        insured_guarantees: [],
        location_guarantees: [],
      });
    }

    if (formDataFlow.insurance_formula === '112') {
      updateFormData({
        location_guarantees: [],
        insured_guarantees: [],
      });
    }
  }, [formDataFlow.insurance_formula]);

  if (loading) {
    return <PageLoading />;
  }

  if (loadingQuotes) {
    return <QuoteGenerateLoading countNr={45} />;
  }

  const stepsQuote = [
    'Dati Contrattuali',
    'Dati Assicurato',
    'Dati Ubicazione',
    'Garanzie',
  ];

  return (
    <HomeFormFlowStyles>
      <V2FontImport />
      <HomeFormStyles>
        <div className="d-flex align-content-center justify-content-center">
          <ProgressBar activeStep={index} steps={stepsQuote} />
        </div>
        {/* Title removed — the v2 ProgressBar already shows the step name
          (steps list on desktop/tablet, "Passo X di N · label" on mobile).
        <CenteredDivTitle className="d-flex align-items-center justify-content-center gap-3 mt-3">
          <div className="row title-questionnaire">
            <div className="quote-h2 divider">
              <h2>{titles[index]}</h2>
            </div>
          </div>
        </CenteredDivTitle> */}
        <div className="travel-tutorial">
          <div className="form-container ">

            {index === 1 && (
              <DatiContrattualiStep
                register={register}
                errors={errors}
                isValid={isValid}
                formDataFlow={formDataFlow}
                updateFormData={updateFormData}
                municipalities={municipalities}
                formData={formData}
                states={states}
              />
            )}

            {(index === 2 && formDataFlow.insurance_formula !== '113') && (
              <DatiAssicuratoStep
                formDataFlow={formDataFlow}
                fields={fields}
                replace={replace}
                update={update}
              />
            )}

            {index === 2 && formDataFlow.insurance_formula === '113' && (
              <>
                <div className="alert alert-info shadow-sm" role="alert">
                  <h5 className="alert-heading mb-2">
                    Nessuna garanzia aggiuntiva disponibile
                  </h5>
                  <p className="mb-0">
                    Per la formula selezionata non sono previste coperture opzionali.
                    Procedi con il click su "Continua" per il prossimo step.
                  </p>
                </div>
              </>
            )}

            {index === 3 && (
              <DatiUbicazioneStep
                register={register}
                errors={errors}
                isValid={isValid}
                formDataFlow={formDataFlow}
                updateFormData={updateFormData}
                municipalities={municipalities}
                setValue={setValue}
              />
            )}

            {(index === 4 && formDataFlow.insurance_formula !== '112') && (
              <GaranzieStep
                formDataFlow={formDataFlow}
                control={control}
                fields={fieldsLocation}
                replace={replaceLocation}
                update={updateLocation}
                watchedGuarantees={watchedGuarantees}
              />
            )}

            {index === 4 && formDataFlow.insurance_formula === '112' && (
              <>
                <div className="alert alert-info shadow-sm" role="alert">
                  <h5 className="alert-heading mb-2">
                    Nessuna garanzia aggiuntiva disponibile
                  </h5>
                  <p className="mb-0">
                    Per la formula selezionata non sono previste coperture opzionali.
                    Procedi con il click su "Vai ai preventivi" per visualizzare il tuo preventivo personalizzato.
                  </p>
                </div>
              </>
            )}

          </div>
        </div>
        {(index === 1 && formDataFlow.insurance_formula !== '') && (
          <FormLayout>
            <div className="terms">
              <SurfaceCardHeading>Informativa Privacy e IVASS</SurfaceCardHeading>

              <div className="text-decoration-underline mt-1 mb-2">
                <a
                  onClick={() => greeniaPrivacyPolicy()}
                  style={{ cursor: 'pointer' }}
                >
                  <small>Greenia Privacy Policy</small>
                </a>
              </div>
              {dataPrivacy.map((privacy, i) => (
                <InputPrivacy
                  required={privacy.required}
                  label={privacy.content}
                  name={privacy.type}
                  id={privacy.type}
                  key={i}
                  checked={formDataFlow?.privacies?.includes(privacy.id)}
                  hasError={showPrivacyErrors && privacy.required === 1 && !formDataFlow?.privacies?.includes(privacy.id)}
                  onChange={(value) => privacyItemChangeHandler(value, privacy)}
                />
              ))}
            </div>
          </FormLayout>
        )}

        {formDataFlow.insurance_formula !== '' && index >= 1 && index <= 4 && (
          <FooterNav className="footer-buttons">
            <div className="d-flex justify-content-between">
              {index !== 1 && (
                <SecondaryButton
                  type="button"
                  className="btn btn-questionnaire"
                  onClick={prevButton}
                >
                  Indietro
                </SecondaryButton>
              )}

              <PrimaryButton
                type="button"
                className="btn btn-questionnaire"
                onClick={nextButton}
                id="continueStep2"
              >
                {index === 4 ? 'Vai ai preventivi' : 'Continua'}
              </PrimaryButton>

            </div>
          </FooterNav>
        )}
      </HomeFormStyles>
    </HomeFormFlowStyles>
  );
}

export default HomeFormFlow;
